import React, { useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import type { DeliveryMetrics, PipelineMetrics } from '../../../support/campaignLeader';
import { formatPipelineUsd } from '../../../support/campaignLeader';
import type { EnrollmentMetrics } from '../../../support/campaignEnrollmentLeader';
import { deliveryStatusSeries, enrollmentStatusSeries } from '../../../support/campaignLeaderCharts';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import CampaignChartCard, { ChartGrid, KpiCards } from './CampaignChartCard';
import PipelineStatusTable from './PipelineStatusTable';

interface Props {
  deliveries: DeliveryMetrics;
  pipeline: PipelineMetrics;
  /** Enablement figure; null until it has loaded. */
  enrollment: EnrollmentMetrics | null;
  enrollmentLoading?: boolean;
  /** "All campaigns" or the selected campaign's name — names the scope of every number below. */
  scopeLabel: string;
  /** Unique per usage so chart tiles don't collide. */
  keyPrefix: string;
  /** Shown above the Enablement figures, e.g. that they are not filtered by the time period. */
  enablementNote?: string;
}

/**
 * The Campaigns tab's two dashboard sections — Deliveries and Enablement progress (one-time work) —
 * for a team (a leader's subtree) or one person. Every figure is passed in already scoped to the
 * selected campaign and computed from the same data as the team table beside it, so the dashboard
 * and the table can't disagree about which campaign they describe.
 */
export default function CampaignsDashboardSections({
  deliveries,
  pipeline,
  enrollment,
  enrollmentLoading = false,
  scopeLabel,
  keyPrefix,
  enablementNote,
}: Props) {
  const [deliveriesOpen, setDeliveriesOpen] = useState(true);
  const [enablementOpen, setEnablementOpen] = useState(true);

  return (
    <>
      <StbAccordion
        variant="minimal"
        label={`Deliveries — ${scopeLabel}`}
        isOpen={deliveriesOpen}
        toggleIsOpen={setDeliveriesOpen}
      >
        <div style={{ padding: 12 }}>
          <KpiCards
            kpis={[
              { label: 'Total deliveries', value: deliveries.total },
              { label: 'Active', value: deliveries.active },
              { label: 'Completed', value: deliveries.byStatus.completed },
              { label: 'Total pipeline', value: formatPipelineUsd(pipeline.total) },
            ]}
          />
          <ChartGrid>
            <CampaignChartCard
              title="Deliveries by status"
              subtitle={scopeLabel}
              chartType="donut_chart"
              data={deliveryStatusSeries(deliveries)}
              tileKey={`${keyPrefix}-deliveries-status`}
              tooltipKeys={{ label: 'Status', value: 'Deliveries' }}
              empty="No deliveries yet."
            />
            <PipelineStatusTable metrics={pipeline} subtitle={`${scopeLabel} — sessions and attributed $ per status`} />
          </ChartGrid>
        </div>
      </StbAccordion>

      <StbAccordion
        variant="minimal"
        label={`Enablement progress (one-time work) — ${scopeLabel}`}
        isOpen={enablementOpen}
        toggleIsOpen={setEnablementOpen}
      >
        <div style={{ padding: 12 }}>
          {enrollmentLoading || !enrollment ? (
            <HbrSpinner>Loading enablement…</HbrSpinner>
          ) : (
            <>
              {enablementNote ? (
                <div className="hbr-css__text-secondary-sm" style={{ marginBottom: 8 }}>
                  {enablementNote}
                </div>
              ) : null}
              <KpiCards
                kpis={[
                  { label: 'Enrolled', value: enrollment.total },
                  { label: 'Not started', value: enrollment.byStatus.not_started },
                  { label: 'In progress', value: enrollment.byStatus.in_progress },
                  { label: 'Completed', value: enrollment.byStatus.completed },
                  { label: 'Avg completion', value: `${enrollment.avgPercent}%` },
                ]}
              />
              <ChartGrid>
                <CampaignChartCard
                  title="Enrollments by status"
                  subtitle={scopeLabel}
                  chartType="donut_chart"
                  data={enrollmentStatusSeries(enrollment)}
                  tileKey={`${keyPrefix}-enrollment-status`}
                  tooltipKeys={{ label: 'Status', value: 'Enrollments' }}
                  empty="No enrollments yet."
                />
              </ChartGrid>
            </>
          )}
        </div>
      </StbAccordion>
    </>
  );
}
